/**
 * Stock Movements API Service
 * @see US-11.8 - Page Mouvements Stock
 */

import { apiClient } from '@/api/client';
import type {
  StockMovementsListResponse,
  StockMovementDetailResponse,
  StockMovementWithProduct,
  CurrentStockResponse,
  GeneratePOSMovementsResponse,
  CreateStockMovementDto,
  QueryStockMovementsDto,
  QueryStockDto,
  GeneratePOSMovementsDto,
  ProductStockHistoryResponse,
} from '@bstock/shared';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get list of stock movements
 */
export async function getStockMovements(
  params?: QueryStockMovementsDto
): Promise<StockMovementsListResponse> {
  const response = await apiClient.get<ApiResponse<StockMovementsListResponse['data']>>(
    '/stock-movements',
    { params }
  );
  return { data: response.data.data, pagination: response.data.pagination! };
}

/**
 * Get movement details
 */
export async function getStockMovement(id: string): Promise<StockMovementDetailResponse> {
  const response = await apiClient.get<ApiResponse<StockMovementDetailResponse>>(
    `/stock-movements/${id}`
  );
  return response.data.data;
}

/**
 * Create a manual movement
 */
export async function createStockMovement(
  data: CreateStockMovementDto
): Promise<StockMovementWithProduct> {
  const response = await apiClient.post<ApiResponse<StockMovementWithProduct>>(
    '/stock-movements',
    data
  );
  return response.data.data;
}

/**
 * Get current stock for products
 */
export async function getCurrentStock(params?: QueryStockDto): Promise<CurrentStockResponse> {
  const response = await apiClient.get<ApiResponse<CurrentStockResponse['data']>>(
    '/stock-movements/stock',
    { params }
  );
  return { data: response.data.data, pagination: response.data.pagination! };
}

/**
 * Get stock history for a product
 */
export async function getProductStockHistory(
  rawProductId: string,
  page?: number,
  limit?: number
): Promise<ProductStockHistoryResponse> {
  const response = await apiClient.get<ApiResponse<ProductStockHistoryResponse['data']>>(
    `/stock/${rawProductId}/history`,
    { params: { page, limit } }
  );
  return { data: response.data.data, pagination: response.data.pagination! };
}

/**
 * Generate POS movements (backfill)
 */
export async function generatePOSMovements(
  data?: GeneratePOSMovementsDto
): Promise<GeneratePOSMovementsResponse> {
  const response = await apiClient.post<ApiResponse<GeneratePOSMovementsResponse>>(
    '/stock-movements/generate-pos',
    data ?? {}
  );
  return response.data.data;
}
